import { styled } from '@linaria/react';
import { useCallback } from 'react';
import { Navigate, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import { BrandedLoadingOverlay } from '../../components/loader/branded-loader';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useGlobalStore } from '../../store/global-store/global-store';
import type { ToolApproval, ToolApprovalDecision } from '../flora/flora.type';
import { FloraPageViewer } from '../flora-pages/flora-page-viewer';
import { FloraPages } from '../flora-pages/flora-pages';
import { AgentAutomationBuilder } from './components/agent-automations/agent-automation-builder';
import { AgentAutomationRunsView } from './components/agent-automations/agent-automation-runs-view';
import { AgentAutomationsView } from './components/agent-automations/agent-automations-view';
import { AgentChatView } from './components/agent-chat-view';
import { AgentCommandsView } from './components/agent-commands/agent-commands-view';
import { AgentDocumentsView } from './components/agent-document/agent-documents-view';
import { useOAuthPopupAutoClose } from './components/agent-integrations/agent-integrations.hooks';
import { AgentLanding } from './components/agent-landing/agent-landing';
import { AgentSettingsLayout } from './components/agent-settings/agent-settings-layout';
import {
  FLORA_SETTINGS_NAV_GROUPS,
  FLORA_SETTINGS_PATHS,
  getRenamedFloraSettingsSection,
  isFloraSettingsSection,
} from './components/agent-settings/agent-settings.constants';
import { FloraSettingsSection } from './components/agent-settings/agent-settings.type';
import { GrantAccessView } from './components/grant-access-view';
import type { FloraAgentOutletContext } from './flora-agent';

/**
 * Outranks the `Flora - Bloomfilter` title that FloraAgentLayout claims for the
 * whole section, so each sub-page gets its own tab name. The layout keeps its
 * claim as the fallback, which is what the chat routes (and any future
 * untitled sub-route) fall back to, and what is restored when a titled
 * sub-page is navigated away from.
 */
const FLORA_SUBPAGE_TITLE_PRIORITY = 1;

/**
 * Route wrapper for the automations list.
 *
 * @returns {JSX.Element} The Flora automations view.
 */
export function FloraAgentAutomationsRoute() {
  useDocumentTitle('Automations - Flora - Bloomfilter', FLORA_SUBPAGE_TITLE_PRIORITY);

  return <AgentAutomationsView />;
}

/**
 * Route wrapper for a single automation's run history.
 *
 * @returns {JSX.Element} The automation runs view.
 */
export function FloraAgentAutomationRunsRoute() {
  useDocumentTitle('Automation Runs - Flora - Bloomfilter', FLORA_SUBPAGE_TITLE_PRIORITY);

  return <AgentAutomationRunsView />;
}

/**
 * Route wrapper for the automation builder, serving both the create (`new`) and
 * edit (`:workflowId/edit`) routes; the presence of a `workflowId` distinguishes
 * them, for the title as well as the close destination.
 *
 * @returns {JSX.Element} The automation builder.
 */
export function FloraAgentAutomationBuilderRoute() {
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId?: string }>();
  const automationsPath = '/application/flora-agent/automations';

  useDocumentTitle(
    workflowId ? 'Edit Automation - Flora - Bloomfilter' : 'New Automation - Flora - Bloomfilter',
    FLORA_SUBPAGE_TITLE_PRIORITY,
  );

  // Editing returns to the automation's runs/detail page; creating (no
  // workflowId) returns to the list. Deleting always returns to the list since
  // the runs/detail page would 404 on the just-removed workflow.
  return (
    <AgentAutomationBuilder
      workflowId={workflowId ?? null}
      onClose={() => navigate(workflowId ? `${automationsPath}/${workflowId}` : automationsPath)}
      onDeleted={() => navigate(automationsPath)}
    />
  );
}

/**
 * Route wrapper for the Flora chat, covering both the index route and a
 * specific `:chatId`. Shows the landing prompt until the conversation has its
 * first question, then the full chat transcript.
 *
 * Claims no document title, so the section falls back to FloraAgentLayout's.
 *
 * @returns {JSX.Element} The landing prompt or the chat transcript.
 */
export function FloraAgentChatRoute() {
  const ctx = useOutletContext<FloraAgentOutletContext>();
  const { setQuestion, submitQuestion, handleAnswerSubmit, handleToolApproval } = ctx;
  const isLanding = !ctx.chatId && ctx.questions.length === 0;
  const chatName = ctx.chatHistories.find((h) => h.id === ctx.chatId)?.description;

  // Hoist the message-bubble handlers so AgentMessageBubble's memo() isn't defeated
  // by fresh inline closures each render. The outlet context object is recreated
  // every render, so these depend on the stable methods, not on `ctx` itself.
  const onFollowupClick = useCallback(
    (prompt: string) => {
      setQuestion(prompt);
      submitQuestion(prompt);
    },
    [setQuestion, submitQuestion],
  );
  const onAnswerSubmit = useCallback(
    (jobId: string, answers: Record<string, string | string[]>) => {
      handleAnswerSubmit(jobId, answers);
    },
    [handleAnswerSubmit],
  );
  const onToolApproval = useCallback(
    (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => {
      handleToolApproval(approvalDecision, toolApproval);
    },
    [handleToolApproval],
  );
  // Hoisted so AgentMessageBubble's memo() isn't defeated by a fresh closure
  // each render (the outlet context object is recreated every render).
  const onPreviewDocument = useCallback(
    (documentId: string) => {
      ctx.onPreviewDocument(documentId);
    },
    [ctx.onPreviewDocument],
  );

  if (isLanding) {
    return (
      <AgentLanding
        question={ctx.question}
        onQuestionChange={ctx.setQuestion}
        onSubmit={(msg, displayMsg) => ctx.submitQuestion(msg, displayMsg)}
        isActiveQuestion={ctx.isActiveQuestion}
        attachments={ctx.attachments}
        threadId={ctx.threadId}
        dataTheme={ctx.isDarkMode ? 'dark' : undefined}
      />
    );
  }

  return (
    <AgentChatView
      questions={ctx.questions}
      messages={ctx.messages}
      isWaitingForFirstResponse={ctx.isWaitingForFirstResponse}
      isActiveQuestion={ctx.isActiveQuestion}
      loadingMessage={ctx.loadingMessage}
      question={ctx.question}
      onQuestionChange={ctx.setQuestion}
      onSubmit={(msg, displayMsg) => ctx.submitQuestion(msg, displayMsg)}
      onNewChat={ctx.handleNewChat}
      chatHistories={ctx.chatHistories}
      threadId={ctx.threadId}
      latestTurnIsLive={ctx.latestTurnIsLive}
      tokenUsage={ctx.tokenUsage}
      dataTheme={ctx.isDarkMode ? 'dark' : undefined}
      chatName={chatName}
      artifactPanelOpen={ctx.artifactPanelOpen}
      onToggleArtifactPanel={ctx.onToggleArtifactPanel}
      artifacts={ctx.allArtifacts}
      documentPanelOpen={ctx.documentPanelOpen}
      onOpenGoogleFile={ctx.onOpenGoogleFile}
      onSelectGoogleFile={ctx.onSelectGoogleFile}
      selectedGoogleFileId={ctx.selectedGoogleFileId}
      onCloseFilesPanel={ctx.onCloseFilesPanel}
      documents={ctx.allDocuments}
      documentRevisions={ctx.allDocumentRevisions}
      pagePanelOpen={ctx.pagePanelOpen}
      onTogglePagePanel={ctx.onTogglePagePanel}
      hasPages={ctx.hasPages}
      selectedPageId={ctx.selectedPageId}
      docPreviewPanelOpen={ctx.docPreviewPanelOpen}
      onPreviewDocument={onPreviewDocument}
      onSelectPreviewDoc={ctx.onSelectPreviewDoc}
      previewableDocs={ctx.previewableDocs}
      selectedPreviewDocId={ctx.selectedPreviewDocId}
      onFollowupClick={onFollowupClick}
      onAnswerSubmit={onAnswerSubmit}
      onToolApproval={onToolApproval}
      attachments={ctx.attachments}
      onStop={ctx.stopAgent}
    />
  );
}

/**
 * Route wrapper for the documents view.
 *
 * @returns {JSX.Element} The Flora documents view.
 */
export function FloraAgentDocumentsRoute() {
  const { handleHistoryClick } = useOutletContext<FloraAgentOutletContext>();
  useDocumentTitle('Documents - Flora - Bloomfilter', FLORA_SUBPAGE_TITLE_PRIORITY);

  return <AgentDocumentsView onGoToChat={handleHistoryClick} />;
}

/**
 * Legacy Preferences route: redirects to the settings Profile section, keeping
 * the query string and hash, so old links and bookmarks keep working.
 *
 * @returns {JSX.Element} A redirect to the Profile settings section.
 */
export function FloraAgentPreferencesRoute() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: FLORA_SETTINGS_PATHS.profile, search, hash }} replace />;
}

/**
 * Route wrapper for the saved-commands page.
 *
 * @returns {JSX.Element} The Flora commands view.
 */
export function FloraAgentCommandsRoute() {
  useDocumentTitle('Commands - Flora - Bloomfilter', FLORA_SUBPAGE_TITLE_PRIORITY);

  return <AgentCommandsView />;
}

/**
 * Legacy Customize route: redirects to the settings General section, keeping
 * the query string and hash, so old links and bookmarks keep working.
 *
 * @returns {JSX.Element} A redirect to the General settings section.
 */
export function FloraAgentCustomizeRoute() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: FLORA_SETTINGS_PATHS.general, search, hash }} replace />;
}

/**
 * Legacy Integrations route: redirects to the settings Connections section,
 * keeping the query string and hash. The Google OAuth callback still lands here inside
 * its popup (`?oauth_popup=1`), so in that case the window closes itself
 * instead of redirecting.
 *
 * @returns {JSX.Element} A redirect to Connections, or a short notice in case the browser refuses to close the popup.
 */
export function FloraAgentIntegrationsRoute() {
  const location = useLocation();
  const isOAuthPopup = useOAuthPopupAutoClose();

  if (isOAuthPopup) return <PopupNotice>Connected. You can close this window.</PopupNotice>;

  return (
    <Navigate
      to={{ pathname: FLORA_SETTINGS_PATHS.connections, search: location.search, hash: location.hash }}
      replace
    />
  );
}

/**
 * Route wrapper for the Flora settings area. Resolves the `:section` (and, for
 * a connection's detail page, `:appId`) params, sends a renamed section to its
 * new name, and sends a missing or unknown section to Profile.
 *
 * @returns {JSX.Element} The settings layout for the requested section, or a redirect.
 */
export function FloraAgentSettingsRoute() {
  const { section, appId } = useParams<{ section?: string; appId?: string }>();
  const resolvedSection = appId ? FloraSettingsSection.Connections : section;
  const isKnownSection = isFloraSettingsSection(resolvedSection);
  const navItem = FLORA_SETTINGS_NAV_GROUPS.flatMap((group) => group.items).find(
    (item) => item.section === resolvedSection,
  );

  useDocumentTitle(
    navItem ? `${navItem.label} - Flora Settings - Bloomfilter` : 'Flora Settings - Bloomfilter',
    FLORA_SUBPAGE_TITLE_PRIORITY,
  );

  const renamedTo = !appId && section ? getRenamedFloraSettingsSection(section) : null;
  if (renamedTo) {
    return <Navigate to={FLORA_SETTINGS_PATHS[renamedTo]} replace />;
  }

  if (!isKnownSection) {
    return <Navigate to={FLORA_SETTINGS_PATHS.profile} replace />;
  }

  return <AgentSettingsLayout section={resolvedSection} appId={appId ?? null} />;
}

/**
 * Route wrapper for the grant-access flow the Google Picker returns into.
 *
 * @returns {JSX.Element} The grant-access view.
 */
export function FloraAgentGrantAccessRoute() {
  useDocumentTitle('Grant Access - Flora - Bloomfilter', FLORA_SUBPAGE_TITLE_PRIORITY);

  return <GrantAccessView />;
}

/**
 * Route wrapper for the Pages gallery. Shows the branded loader until global
 * state hydrates on a cold load, then renders the gallery. FloraPages already
 * handles a null organization gracefully, so this guard is a cosmetic hold to
 * preserve the branded cold-load UX on a deep-linked or bookmarked page URL,
 * not a crash guard.
 *
 * Unlike the sibling routes above, the document title is claimed by FloraPages
 * itself, so the loader hold falls back to the layout's `Flora - Bloomfilter`.
 *
 * @returns {JSX.Element} A loader while global state hydrates, then the Pages
 *   gallery view.
 */
export function FloraAgentPagesRoute() {
  const isGlobalStateInitialized = useGlobalStore((state) => state.initialized);
  if (!isGlobalStateInitialized) return <BrandedLoadingOverlay visible variant="colored" />;
  return <FloraPages />;
}

/**
 * Route wrapper for the full-window published-page viewer.
 *
 * Mirrors FloraAgentPagesRoute: holds the branded loader until global state
 * hydrates so a valid shared page link keeps the branded cold-load UX rather
 * than flashing before the organization resolves. The document title is
 * likewise claimed by FloraPageViewer itself.
 *
 * @returns {JSX.Element} A loader while global state hydrates, then the page
 *   viewer.
 */
export function FloraAgentPageViewerRoute() {
  const isGlobalStateInitialized = useGlobalStore((state) => state.initialized);
  if (!isGlobalStateInitialized) return <BrandedLoadingOverlay visible variant="colored" />;
  return <FloraPageViewer />;
}

const PopupNotice = styled.p`
  margin: 0;
  padding: 48px 24px;
  text-align: center;
  font-size: var(--font-size-regular);
  color: var(--ink-dark);
`;
